import { Card, CardContent } from '@/components/ui/card';
import { InspectionPill as Pill } from '@/components/inspections/InspectionChip';
import { InspectionPhoto } from '@/components/inspections/InspectionPhoto';
import {
    CONDITION_LABELS,
    CONDITION_TONES,
    GRADE_LABELS,
    GRADE_TONES,
    RESULT_LABELS,
    carriedLabel,
    formatKm,
    formatKmChange,
    inspectionStatus,
    itemLabel,
    releaseNote,
    roundLabel,
    shortDay,
    type InspectionChecklist,
    type InspectionCurrent,
    type InspectionOpenItem,
    type InspectionVisit,
} from '@/lib/inspections';

function OpenItemRow({ item, checklist }: { item: InspectionOpenItem; checklist: InspectionChecklist }) {
    const label = itemLabel(checklist, item.item);
    const meta = [
        `Open since ${shortDay(item.raised_on) ?? '-'}`,
        carriedLabel(item.times_carried),
        item.grade === 'fix_soon' ? 'To fix by the next inspection' : null,
        item.raised_before_contract ? 'Raised before this contract' : null,
    ].filter(Boolean);

    return (
        <li className="space-y-2 rounded-md border p-3">
            <div className="flex items-start justify-between gap-3">
                <p className="text-sm font-medium">{label}</p>
                <Pill tone={GRADE_TONES[item.grade]} className="shrink-0">{GRADE_LABELS[item.grade]}</Pill>
            </div>
            {item.note && <p className="text-sm">{item.note}</p>}
            <p className="text-xs text-muted-foreground">{meta.join(' · ')}</p>
            {item.photos.length > 0 && (
                <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                    {item.photos.map(photo => (
                        <InspectionPhoto key={photo.id} url={photo.url} label={label} />
                    ))}
                </div>
            )}
        </li>
    );
}

interface InspectionStatusCardProps {
    current: InspectionCurrent;
    openItems: InspectionOpenItem[];
    checklist: InspectionChecklist;
    /** The same payload's visits, newest first: the round's monthly, the last visit and the release are read from them. */
    visits: InspectionVisit[];
}

/**
 * This round's status for the car, in the partner's wording (plan section 8), then the last
 * inspection's date, result and odometer, then the car's condition and its open items, Must fix
 * first. Every state comes from `current`; the card only chooses the words.
 */
export function InspectionStatusCard({ current, openItems, checklist, visits }: InspectionStatusCardProps) {
    const status = inspectionStatus(current);
    const round = roundLabel(current);
    const released = releaseNote(current, visits);
    const mustFix = openItems.filter(i => i.grade === 'fix_now');
    const fixSoon = openItems.filter(i => i.grade === 'fix_soon');

    // The facts row: the round's monthly when the round has one, else the car's last visit of any
    // kind, so a Missed or Not yet card still says when the car was last seen and what it read.
    const roundVisit = current.inspection_id !== null ? visits.find(v => v.id === current.inspection_id) ?? null : null;
    const lastVisit = visits[0] ?? null;
    const facts = current.inspection_id !== null
        ? { dateLabel: 'Inspected', day: shortDay(current.inspected_on), result: current.result, visit: roundVisit }
        : lastVisit
            ? { dateLabel: 'Last inspection', day: shortDay(lastVisit.inspected_on), result: lastVisit.result, visit: lastVisit }
            : null;
    const odometer = formatKm(facts?.visit?.odometer_km);
    const odometerChange = formatKmChange(facts?.visit?.odometer_change_km);

    return (
        <Card>
            <CardContent className="space-y-5 p-4 sm:p-6">
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <h2 className="text-sm font-normal text-muted-foreground">{round ?? 'Monthly inspections'}</h2>
                    {/* A Held title already names the condition; the pill would say it twice. */}
                    {current.condition && status.key !== 'held' && (
                        <Pill tone={CONDITION_TONES[current.condition]}>
                            Condition: {CONDITION_LABELS[current.condition]}
                        </Pill>
                    )}
                </div>

                <div className="space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                        <Pill tone={status.tone} className="text-sm">{status.title}</Pill>
                        {current.late && <Pill tone="warning">Late</Pill>}
                    </div>
                    <p className="text-base leading-relaxed">{status.sentence}</p>
                    {released && <p className="text-sm text-muted-foreground">{released}</p>}
                </div>

                {facts && (
                    <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm sm:grid-cols-3">
                        <div>
                            <dt className="text-xs text-muted-foreground">{facts.dateLabel}</dt>
                            <dd className="font-medium">{facts.day ?? '-'}</dd>
                        </div>
                        {facts.result && (
                            <div>
                                <dt className="text-xs text-muted-foreground">Result</dt>
                                <dd className="font-medium">{RESULT_LABELS[facts.result]}</dd>
                            </div>
                        )}
                        {odometer && (
                            <div>
                                <dt className="text-xs text-muted-foreground">Odometer</dt>
                                <dd className="font-medium tabular-nums">
                                    {odometer}
                                    {odometerChange && (
                                        <span className="ml-1 text-xs font-normal text-muted-foreground">({odometerChange})</span>
                                    )}
                                </dd>
                            </div>
                        )}
                    </dl>
                )}

                {current.condition && (
                    <section aria-label="Open items" className="space-y-4 border-t pt-4">
                        {openItems.length === 0 ? (
                            <p className="text-sm text-muted-foreground">Nothing is open on this car.</p>
                        ) : (
                            <>
                                {mustFix.length > 0 && (
                                    <div className="space-y-2">
                                        <h3 className="text-sm font-semibold">Must fix ({mustFix.length})</h3>
                                        <ul className="space-y-2">
                                            {mustFix.map(item => <OpenItemRow key={item.line_id} item={item} checklist={checklist} />)}
                                        </ul>
                                    </div>
                                )}
                                {fixSoon.length > 0 && (
                                    <div className="space-y-2">
                                        <h3 className="text-sm font-semibold">Fix soon ({fixSoon.length})</h3>
                                        <ul className="space-y-2">
                                            {fixSoon.map(item => <OpenItemRow key={item.line_id} item={item} checklist={checklist} />)}
                                        </ul>
                                    </div>
                                )}
                            </>
                        )}
                    </section>
                )}
            </CardContent>
        </Card>
    );
}
